// components/Dashboard/DashboardToolbar.jsx
import React, { useState } from 'react';
import { RefreshCw, Eye, EyeOff, ChevronDown, Clock, Save, LayoutGrid } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { dashboardsApi, annotationsApi } from '../../services/api';
import { DashboardTemplates, TEMPLATES } from './DashboardTemplates';
import toast from 'react-hot-toast';

const TIME_PRESETS = ['5m', '15m', '30m', '1h', '6h', '24h'];
const REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '5s',  value: 5  },
  { label: '10s', value: 10 },
  { label: '30s', value: 30 },
  { label: '1m',  value: 60 },
];
const SOURCES    = ['ALL', 'GCP', 'AZURE', 'MONGODB', 'DATABASE'];
const SEVERITIES = ['ALL', 'CRITICAL', 'WARNING', 'INFO'];
const ENVS       = ['production', 'staging', 'development'];

function Select({ value, onChange, options, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ position: 'relative' }}>
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{
            appearance: 'none',
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            color: 'var(--text)',
            fontSize: 11,
            fontFamily: "'Space Mono', monospace",
            padding: '4px 22px 4px 8px',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          {options.map(o => (
            <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
          ))}
        </select>
        <ChevronDown size={10} style={{
          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--text-3)', pointerEvents: 'none',
        }} />
      </div>
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 20, background: 'var(--border)', flexShrink: 0 }} />;
}

export function DashboardToolbar({ onRefresh, loading }) {
  const {
    timeRange, setTimeRange,
    autoRefresh, setAutoRefresh,
    variables, setVariable,
    showAnnotations, toggleAnnotations,
    dashboardSaved, markDashboardSaved,
  } = useStore();

  const { activeTemplate } = useStore();
  const [addingAnnotation, setAddingAnnotation] = useState(false);
  const [annotationForm, setAnnotationForm] = useState({ title: '', type: 'event', color: '#f59e0b' });
  const [showTemplates, setShowTemplates] = useState(false);

  async function handleSaveDashboard() {
    try {
      await dashboardsApi.update('main', {
        name: 'ObserveAI Main Dashboard',
        timeRange: { preset: timeRange },
        variables,
        message: `Saved at ${new Date().toLocaleTimeString()}`,
      });
      markDashboardSaved();
      toast.success('Dashboard saved');
    } catch {
      // Might not exist yet — create it
      try {
        await dashboardsApi.create({
          name: 'ObserveAI Main Dashboard',
          uid: 'main',
          timeRange: { preset: timeRange },
          variables,
        });
        markDashboardSaved();
        toast.success('Dashboard saved');
      } catch (err) {
        toast.error('Could not save dashboard');
      }
    }
  }

  async function submitAnnotation() {
    try {
      const a = await annotationsApi.create({
        ...annotationForm,
        time: new Date().toISOString(),
      });
      useStore.getState().addAnnotation(a);
      toast.success('Annotation added');
    } catch {
      // Demo mode: add locally
      useStore.getState().addAnnotation({ ...annotationForm, time: new Date().toISOString(), _id: `demo-${Date.now()}` });
      toast.success('Annotation added (demo)');
    }
    setAddingAnnotation(false);
    setAnnotationForm({ title: '', type: 'event', color: '#f59e0b' });
  }

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
        padding: '10px 16px',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border)',
      }}>
        {/* Variables */}
        <Select label="Source" value={variables.source} options={SOURCES}
          onChange={v => setVariable('source', v)} />
        <Select label="Severity" value={variables.severity} options={SEVERITIES}
          onChange={v => setVariable('severity', v)} />
        <Select label="Env" value={variables.environment} options={ENVS}
          onChange={v => setVariable('environment', v)} />

        <Divider />

        {/* Time range presets */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Clock size={12} style={{ color: 'var(--text-3)' }} />
          <div style={{
            display: 'flex', borderRadius: 6, overflow: 'hidden',
            border: '1px solid var(--border)',
          }}>
            {TIME_PRESETS.map(p => (
              <button key={p} onClick={() => setTimeRange(p)}
                style={{
                  padding: '4px 10px', fontSize: 11, border: 'none', cursor: 'pointer',
                  fontFamily: "'Space Mono', monospace",
                  background: timeRange === p ? 'rgba(59,130,246,.3)' : 'var(--bg-tertiary)',
                  color: timeRange === p ? '#60a5fa' : 'var(--text-3)',
                  borderRight: '1px solid var(--border)',
                  transition: 'all .15s',
                }}>
                {p}
              </button>
            ))}
          </div>
        </div>

        <Divider />

        {/* Auto-refresh */}
        <Select
          label="Refresh"
          value={autoRefresh}
          options={REFRESH_OPTIONS}
          onChange={v => setAutoRefresh(Number(v))}
        />

        {/* Manual refresh */}
        <button onClick={onRefresh} disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)',
            background: 'var(--bg-tertiary)', color: loading ? 'var(--text-3)' : 'var(--text)',
            cursor: loading ? 'wait' : 'pointer', fontSize: 11,
          }}>
          <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>

        <Divider />

        {/* Annotations toggle */}
        <button onClick={toggleAnnotations}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 6, fontSize: 11,
            border: `1px solid ${showAnnotations ? 'rgba(245,158,11,.4)' : 'var(--border)'}`,
            background: showAnnotations ? 'rgba(245,158,11,.1)' : 'var(--bg-tertiary)',
            color: showAnnotations ? '#fbbf24' : 'var(--text-3)', cursor: 'pointer',
          }}>
          {showAnnotations ? <Eye size={12} /> : <EyeOff size={12} />}
          Annotations
        </button>

        <button onClick={() => setAddingAnnotation(true)}
          style={{
            padding: '4px 10px', borderRadius: 6, fontSize: 11,
            border: '1px solid var(--border)', background: 'var(--bg-tertiary)',
            color: 'var(--text-2)', cursor: 'pointer',
          }}>
          + Mark event
        </button>

        <Divider />

        {/* Templates picker */}
        <button onClick={() => setShowTemplates(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 6, fontSize: 11,
            border: '1px solid rgba(99,102,241,.4)',
            background: 'rgba(99,102,241,.1)',
            color: '#a5b4fc', cursor: 'pointer',
          }}>
          <LayoutGrid size={12} />
          {TEMPLATES.find(t => t.id === activeTemplate)?.name || 'Template'}
        </button>

        {/* Save */}
        <button onClick={handleSaveDashboard}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 6, fontSize: 11, marginLeft: 'auto',
            border: `1px solid ${dashboardSaved ? 'var(--border)' : 'rgba(59,130,246,.5)'}`,
            background: dashboardSaved ? 'var(--bg-tertiary)' : 'rgba(59,130,246,.15)',
            color: dashboardSaved ? 'var(--text-3)' : '#60a5fa', cursor: 'pointer',
          }}>
          <Save size={12} />
          {dashboardSaved ? 'Saved' : 'Save*'}
        </button>
      </div>

      {/* Add annotation modal */}
      {addingAnnotation && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 500,
          background: 'rgba(0,0,0,.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
          onClick={() => setAddingAnnotation(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              borderRadius: 12, padding: 24, width: 340,
              boxShadow: '0 20px 60px rgba(0,0,0,.5)',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 16 }}>Mark Event / Annotation</div>

            {[
              { label: 'Title', key: 'title', type: 'text', placeholder: 'e.g. Deploy v1.2.3' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>{f.label}</label>
                <input
                  type={f.type}
                  placeholder={f.placeholder}
                  value={annotationForm[f.key]}
                  onChange={e => setAnnotationForm(af => ({ ...af, [f.key]: e.target.value }))}
                  style={{
                    width: '100%', padding: '7px 10px', borderRadius: 6,
                    background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
                    color: 'var(--text)', fontSize: 12, outline: 'none',
                  }}
                />
              </div>
            ))}

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>Type</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['event', 'deploy', 'incident', 'alert'].map(t => (
                  <button key={t} onClick={() => setAnnotationForm(af => ({ ...af, type: t }))}
                    style={{
                      padding: '4px 10px', borderRadius: 5, fontSize: 11,
                      border: `1px solid ${annotationForm.type === t ? 'rgba(59,130,246,.5)' : 'var(--border)'}`,
                      background: annotationForm.type === t ? 'rgba(59,130,246,.2)' : 'var(--bg-tertiary)',
                      color: annotationForm.type === t ? '#60a5fa' : 'var(--text-2)',
                      cursor: 'pointer', textTransform: 'capitalize',
                    }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>Color</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['#f59e0b', '#3b82f6', '#ef4444', '#22c55e', '#8b5cf6'].map(c => (
                  <div key={c} onClick={() => setAnnotationForm(af => ({ ...af, color: c }))}
                    style={{
                      width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer',
                      outline: annotationForm.color === c ? `2px solid white` : 'none',
                      outlineOffset: 2,
                    }} />
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setAddingAnnotation(false)}
                style={{
                  padding: '7px 16px', borderRadius: 6, fontSize: 12,
                  border: '1px solid var(--border)', background: 'none',
                  color: 'var(--text-2)', cursor: 'pointer',
                }}>Cancel</button>
              <button onClick={submitAnnotation} disabled={!annotationForm.title}
                style={{
                  padding: '7px 16px', borderRadius: 6, fontSize: 12,
                  border: 'none', background: '#3b82f6',
                  color: 'white', cursor: 'pointer', fontWeight: 600,
                }}>Add</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {showTemplates && <DashboardTemplates onClose={() => setShowTemplates(false)} />}
    </div>
  );
}
